import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../axiosconfing";
import { getRequestWithToken } from "../../utils/ApiRequests";
import ReusableLoader from "../../components/ReusableLoader/ReusableLoader";
import Unauthorized from "../../components/unauthorized/Unauthorized";

const pageSizeOptions = [10, 25, 50, 100];

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "read", label: "Read" },
  { value: "failed", label: "Failed" },
];

const environmentOptions = [
  { value: "", label: "Current Environment" },
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
];

const todayDateInput = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
}).format(new Date());

const getInitialFilters = (searchParams) => ({
  from: searchParams.get("from") || "",
  to: searchParams.get("to") || "",
  campaignId: searchParams.get("campaignId") || "",
  templateName: searchParams.get("templateName") || "",
  status: searchParams.get("status") || "",
  environment: searchParams.get("environment") || "",
  search: searchParams.get("search") || "",
});

const emptyFilters = {
  from: "",
  to: "",
  campaignId: "",
  templateName: "",
  status: "",
  environment: "",
  search: "",
};

const getResourceNames = () => {
  try {
    const storedResources = localStorage.getItem("resourceNames");
    const parsedResources = JSON.parse(storedResources || "[]");
    return Array.isArray(parsedResources) ? parsedResources : [];
  } catch (error) {
    return [];
  }
};

const buildQuery = (filters, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });
  return params.toString();
};

const formatDate = (value) => value ? new Date(value).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-";

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const StatCard = ({ label, value, suffix = "" }) => (
  <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, minHeight: 86 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>{value ?? 0}{suffix}</Typography>
  </Paper>
);

const StatusChip = ({ status }) => {
  const normalized = String(status || "pending").toLowerCase();
  const color = normalized === "failed" ? "error" : normalized === "read" ? "success" : normalized === "delivered" ? "info" : normalized === "sent" ? "primary" : "default";
  return <Chip size="small" color={color} label={normalized} />;
};

const WhatsAppAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamKey = searchParams.toString();
  const canViewAnalytics = getResourceNames().includes("Whatsapp Campaign");
  const [draftFilters, setDraftFilters] = useState(() => getInitialFilters(searchParams));
  const [appliedFilters, setAppliedFilters] = useState(() => getInitialFilters(searchParams));
  const [summary, setSummary] = useState({});
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const query = useMemo(() => buildQuery(appliedFilters), [appliedFilters]);
  const dateRangeError = useMemo(() => {
    if (!draftFilters.from || !draftFilters.to) return "";
    return draftFilters.from > draftFilters.to ? "From date cannot be after To date." : "";
  }, [draftFilters.from, draftFilters.to]);
  const hasDraftChanges = useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters),
    [draftFilters, appliedFilters]
  );
  const appliedFilterCount = useMemo(
    () => Object.values(appliedFilters).filter(Boolean).length,
    [appliedFilters]
  );

  const loadSummary = async () => {
    if (!canViewAnalytics) return;

    setLoading(true);
    setError("");
    setStatusMessage("Loading WhatsApp analytics summary.");
    try {
      const response = await getRequestWithToken(`admin/whatsapp-analytics/summary?${query}`);
      setSummary(response.data?.data || {});
      setStatusMessage("WhatsApp analytics summary updated.");
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to load WhatsApp analytics.");
      setStatusMessage("Unable to load WhatsApp analytics summary.");
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    if (!canViewAnalytics) return;

    setTableLoading(true);
    setError("");
    setStatusMessage("Loading WhatsApp message logs.");
    try {
      const response = await getRequestWithToken(`admin/whatsapp-analytics/logs?${buildQuery(appliedFilters, { page: pagination.page, limit: pagination.limit })}`);
      setLogs(response.data?.data || []);
      setPagination((prev) => ({ ...prev, ...(response.data?.pagination || {}) }));
      setStatusMessage("WhatsApp message logs updated.");
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to load WhatsApp logs.");
      setStatusMessage("Unable to load WhatsApp message logs.");
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const nextFilters = getInitialFilters(searchParams);
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setError("");
  }, [searchParamKey]);

  useEffect(() => { loadSummary(); }, [query, canViewAnalytics]);
  useEffect(() => { loadLogs(); }, [query, pagination.page, pagination.limit, canViewAnalytics]);

  const applyFilters = () => {
    if (dateRangeError) {
      setError(dateRangeError);
      setStatusMessage(dateRangeError);
      return;
    }

    const params = new URLSearchParams();
    Object.entries(draftFilters).forEach(([key, value]) => { if (value) params.set(key, value); });
    setError("");
    setAppliedFilters(draftFilters);
    setSearchParams(params);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setStatusMessage("Filters applied.");
  };

  const resetFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchParams({});
    setPagination((prev) => ({ ...prev, page: 1 }));
    setError("");
    setStatusMessage("Filters reset.");
  };

  const refresh = () => {
    setStatusMessage("Refreshing WhatsApp analytics.");
    loadSummary();
    loadLogs();
  };

  const exportLogs = async () => {
    if (!canViewAnalytics) return;

    setExporting(true);
    setError("");
    try {
      const response = await axiosInstance.get(`admin/whatsapp-analytics/export?${query}`, { responseType: "blob" });
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(new Blob([response.data], { type: response.headers["content-type"] }), `whatsapp-analytics-${today}.xlsx`);
      setStatusMessage("WhatsApp analytics export downloaded.");
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to export WhatsApp logs.");
      setStatusMessage("Unable to export WhatsApp logs.");
    } finally {
      setExporting(false);
    }
  };

  if (!canViewAnalytics) {
    return <Unauthorized />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h5" sx={{ fontWeight: 700 }}>WhatsApp Analytics</Typography>
              {summary.environment ? <Chip size="small" color="info" label={`Env: ${summary.environment}`} /> : null}
              <Chip size="small" label={`${appliedFilterCount} filters applied`} />
              {hasDraftChanges ? <Chip size="small" color="warning" label="Unapplied changes" /> : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">Campaign message delivery and failure logs from the currently connected backend.</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refresh} disabled={loading || tableLoading}>Refresh</Button>
            <Button variant="contained" startIcon={<DownloadIcon />} disabled={exporting} onClick={exportLogs}>{exporting ? "Exporting..." : "Export"}</Button>
          </Stack>
        </Stack>

        <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} inputProps={{ max: todayDateInput() }} value={draftFilters.from} error={Boolean(dateRangeError)} helperText={dateRangeError || " "} onChange={(e) => setDraftFilters((p) => ({ ...p, from: e.target.value }))} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} inputProps={{ max: todayDateInput() }} value={draftFilters.to} error={Boolean(dateRangeError)} helperText={dateRangeError || " "} onChange={(e) => setDraftFilters((p) => ({ ...p, to: e.target.value }))} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" label="Campaign Id" value={draftFilters.campaignId} onChange={(e) => setDraftFilters((p) => ({ ...p, campaignId: e.target.value }))} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" label="Template" value={draftFilters.templateName} onChange={(e) => setDraftFilters((p) => ({ ...p, templateName: e.target.value }))} /></Grid>
            <Grid item xs={12} md={2}><TextField select fullWidth size="small" label="Status" value={draftFilters.status} onChange={(e) => setDraftFilters((p) => ({ ...p, status: e.target.value }))}>{statusOptions.map((item) => <MenuItem key={item.value || "all"} value={item.value}>{item.label}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={2}><TextField select fullWidth size="small" label="Environment" value={draftFilters.environment} onChange={(e) => setDraftFilters((p) => ({ ...p, environment: e.target.value }))}>{environmentOptions.map((item) => <MenuItem key={item.value || "current"} value={item.value}>{item.label}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" label="Search" placeholder="Phone, campaign, reason" value={draftFilters.search} onChange={(e) => setDraftFilters((p) => ({ ...p, search: e.target.value }))} /></Grid>
            <Grid item xs={12}><Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap><Button variant="contained" onClick={applyFilters} disabled={!hasDraftChanges}>Apply</Button><Button variant="outlined" onClick={resetFilters}>Reset</Button></Stack></Grid>
          </Grid>
        </Paper>

        <Box
          sx={{ border: 0, clip: "rect(0 0 0 0)", height: 1, m: -1, overflow: "hidden", p: 0, position: "absolute", whiteSpace: "nowrap", width: 1 }}
          aria-live="polite"
          aria-atomic="true"
        >
          {statusMessage}
        </Box>

        {error && <Alert severity="error" role="alert">{error}</Alert>}

        {loading ? <ReusableLoader label="Loading WhatsApp analytics summary" /> : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Total" value={summary.total} /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Sent" value={summary.sent} /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Delivered" value={summary.delivered} /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Read" value={summary.read} /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Failed" value={summary.failed} /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Pending" value={summary.pending} /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Delivery Rate" value={summary.deliveryRate} suffix="%" /></Grid>
            <Grid item xs={12} sm={6} md={1.5}><StatCard label="Failure Rate" value={summary.failureRate} suffix="%" /></Grid>
          </Grid>
        )}

        <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Message Logs</Typography>
              <Chip size="small" label={`${pagination.total || 0} records`} />
            </Stack>
          </Stack>
          <TableContainer
            sx={{ maxHeight: 560, overflowX: "auto", "&:focus": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}
            tabIndex={0}
            role="region"
            aria-label="Scrollable WhatsApp message logs table"
            aria-busy={tableLoading}
          >
            <Table size="small" stickyHeader aria-label="WhatsApp message logs">
              <TableHead>
                <TableRow>
                  <TableCell scope="col">Date</TableCell>
                  <TableCell scope="col">Campaign</TableCell>
                  <TableCell scope="col">Template</TableCell>
                  <TableCell scope="col">Recipient</TableCell>
                  <TableCell scope="col">Status</TableCell>
                  <TableCell scope="col">Sent At</TableCell>
                  <TableCell scope="col">Delivered At</TableCell>
                  <TableCell scope="col">Read At</TableCell>
                  <TableCell scope="col">Failed At</TableCell>
                  <TableCell scope="col">Failure Reason</TableCell>
                  <TableCell scope="col">Meta Error Code</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading ? (
                  <TableRow><TableCell colSpan={11}><ReusableLoader label="Loading WhatsApp message logs" /></TableCell></TableRow>
                ) : logs.length ? logs.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>{row.campaignName || row.campaignId || "-"}</TableCell>
                    <TableCell>{row.templateName || "-"}</TableCell>
                    <TableCell>{row.recipient || "-"}</TableCell>
                    <TableCell><StatusChip status={row.status} /></TableCell>
                    <TableCell>{formatDate(row.sentAt)}</TableCell>
                    <TableCell>{formatDate(row.deliveredAt)}</TableCell>
                    <TableCell>{formatDate(row.readAt)}</TableCell>
                    <TableCell>{formatDate(row.failedAt)}</TableCell>
                    <TableCell sx={{ minWidth: 260 }}>{row.status === "failed" ? row.failureReason || "-" : "-"}</TableCell>
                    <TableCell>{row.providerErrorCode || "-"}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={11} align="center">No WhatsApp logs found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={Math.max((pagination.page || 1) - 1, 0)}
            rowsPerPage={pagination.limit || 25}
            rowsPerPageOptions={pageSizeOptions}
            onPageChange={(event, page) => setPagination((prev) => ({ ...prev, page: page + 1 }))}
            onRowsPerPageChange={(event) => setPagination({ page: 1, limit: parseInt(event.target.value, 10), total: pagination.total, totalPages: pagination.totalPages })}
          />
        </Paper>
      </Stack>
    </Box>
  );
};

export default WhatsAppAnalytics;
